/**
 * @fileoverview Enhanced currency utility with support for extensive fiat and crypto currencies
 * 
 * Uses external libraries for comprehensive currency symbol support:
 * - currency-symbol-map: Standard fiat currency symbols
 * - cryptocurrency-icons: Crypto currency symbols and info
 * - currency-codes: ISO 4217 currency codes and information
 */

// Import external libraries
let getSymbolFromMap: ((code: string) => string | undefined) | null = null;
let currencyCodesData: unknown = null;

// Lazy load the external libraries to handle potential import issues
const loadCurrencySymbolMap = async () => {
  if (getSymbolFromMap === null) {
    try {
      const symbolMap = await import('currency-symbol-map');
      getSymbolFromMap = symbolMap.default || symbolMap;
    } catch (error) {
      getSymbolFromMap = () => undefined;
    }
  }
  return getSymbolFromMap;
};

const loadCurrencyCodes = async () => {
  if (currencyCodesData === null) {
    try {
      const currencyCodes = await import('currency-codes');
      currencyCodesData = currencyCodes.default || currencyCodes;
    } catch (error) {
      currencyCodesData = { code: () => null };
    }
  }
  return currencyCodesData;
};

// Extended crypto currency information including symbols
const CRYPTO_CURRENCIES = {
  // Major cryptocurrencies
  'BTC': { symbol: '₿', name: 'Bitcoin', decimals: 8 },
  'ETH': { symbol: 'Ξ', name: 'Ethereum', decimals: 8 },
  'LTC': { symbol: 'Ł', name: 'Litecoin', decimals: 8 },
  'XRP': { symbol: 'XRP', name: 'Ripple', decimals: 6 },
  'ADA': { symbol: 'ADA', name: 'Cardano', decimals: 8 },
  'DOT': { symbol: 'DOT', name: 'Polkadot', decimals: 6 },
  'SOL': { symbol: 'SOL', name: 'Solana', decimals: 6 },
  'MATIC': { symbol: 'MATIC', name: 'Polygon', decimals: 6 },
  'AVAX': { symbol: 'AVAX', name: 'Avalanche', decimals: 6 },
  'LINK': { symbol: 'LINK', name: 'Chainlink', decimals: 6 },
  'UNI': { symbol: 'UNI', name: 'Uniswap', decimals: 6 },
  'ATOM': { symbol: 'ATOM', name: 'Cosmos', decimals: 6 },
  'XLM': { symbol: 'XLM', name: 'Stellar', decimals: 6 },
  'VET': { symbol: 'VET', name: 'VeChain', decimals: 6 },
  'ALGO': { symbol: 'ALGO', name: 'Algorand', decimals: 6 },
  'XTZ': { symbol: 'XTZ', name: 'Tezos', decimals: 6 },
  'FIL': { symbol: 'FIL', name: 'Filecoin', decimals: 6 },
  'AAVE': { symbol: 'AAVE', name: 'Aave', decimals: 6 },
  'COMP': { symbol: 'COMP', name: 'Compound', decimals: 6 },
  'MKR': { symbol: 'MKR', name: 'Maker', decimals: 6 },
  'SUSHI': { symbol: 'SUSHI', name: 'SushiSwap', decimals: 6 },
  'CRV': { symbol: 'CRV', name: 'Curve DAO Token', decimals: 6 },
  'SNX': { symbol: 'SNX', name: 'Synthetix', decimals: 6 },
  'YFI': { symbol: 'YFI', name: 'yearn.finance', decimals: 6 },
  'ZEC': { symbol: 'ZEC', name: 'Zcash', decimals: 8 },
  'DASH': { symbol: 'DASH', name: 'Dash', decimals: 8 },
  'XMR': { symbol: 'XMR', name: 'Monero', decimals: 8 },
  'ETC': { symbol: 'ETC', name: 'Ethereum Classic', decimals: 8 },
  'ZIL': { symbol: 'ZIL', name: 'Zilliqa', decimals: 6 },
  'BAT': { symbol: 'BAT', name: 'Basic Attention Token', decimals: 6 },
  'ENJ': { symbol: 'ENJ', name: 'Enjin Coin', decimals: 6 },
  'MANA': { symbol: 'MANA', name: 'Decentraland', decimals: 6 },
  'SAND': { symbol: 'SAND', name: 'The Sandbox', decimals: 6 },
  'AXS': { symbol: 'AXS', name: 'Axie Infinity', decimals: 6 },
  'APE': { symbol: 'APE', name: 'ApeCoin', decimals: 6 },
  'LDO': { symbol: 'LDO', name: 'Lido DAO', decimals: 6 },
  'OP': { symbol: 'OP', name: 'Optimism', decimals: 6 },
  'ARB': { symbol: 'ARB', name: 'Arbitrum', decimals: 6 },
  
  // Stablecoins
  'USDT': { symbol: 'USDT', name: 'Tether', decimals: 2 },
  'USDC': { symbol: 'USDC', name: 'USD Coin', decimals: 2 },
  'BUSD': { symbol: 'BUSD', name: 'Binance USD', decimals: 2 },
  'DAI': { symbol: 'DAI', name: 'Dai', decimals: 2 },
  'FRAX': { symbol: 'FRAX', name: 'Frax', decimals: 2 },
  'TUSD': { symbol: 'TUSD', name: 'TrueUSD', decimals: 2 },
  'USDP': { symbol: 'USDP', name: 'Pax Dollar', decimals: 2 },
  'LUSD': { symbol: 'LUSD', name: 'Liquity USD', decimals: 2 },
  'SUSD': { symbol: 'SUSD', name: 'sUSD', decimals: 2 },
  'GUSD': { symbol: 'GUSD', name: 'Gemini Dollar', decimals: 2 },
  
  // Exchange tokens
  'BNB': { symbol: 'BNB', name: 'Binance Coin', decimals: 6 },
  'FTT': { symbol: 'FTT', name: 'FTX Token', decimals: 6 },
  'CRO': { symbol: 'CRO', name: 'Crypto.com Coin', decimals: 6 },
  'LEO': { symbol: 'LEO', name: 'UNUS SED LEO', decimals: 6 },
  'HT': { symbol: 'HT', name: 'Huobi Token', decimals: 6 },
  'OKB': { symbol: 'OKB', name: 'OKB', decimals: 6 },
  'KCS': { symbol: 'KCS', name: 'KuCoin Shares', decimals: 6 },
  'GT': { symbol: 'GT', name: 'GateToken', decimals: 6 },
} as const;

// Extended fiat currency information
const FIAT_CURRENCIES = {
  'USD': { symbol: '$', name: 'US Dollar', decimals: 2 },
  'EUR': { symbol: '€', name: 'Euro', decimals: 2 },
  'GBP': { symbol: '£', name: 'British Pound', decimals: 2 },
  'JPY': { symbol: '¥', name: 'Japanese Yen', decimals: 0 },
  'CAD': { symbol: 'C$', name: 'Canadian Dollar', decimals: 2 },
  'AUD': { symbol: 'A$', name: 'Australian Dollar', decimals: 2 },
  'CHF': { symbol: 'CHF', name: 'Swiss Franc', decimals: 2 },
  'CNY': { symbol: '¥', name: 'Chinese Yuan', decimals: 2 },
  'SEK': { symbol: 'kr', name: 'Swedish Krona', decimals: 2 },
  'NZD': { symbol: 'NZ$', name: 'New Zealand Dollar', decimals: 2 },
  'MXN': { symbol: '$', name: 'Mexican Peso', decimals: 2 },
  'SGD': { symbol: 'S$', name: 'Singapore Dollar', decimals: 2 },
  'HKD': { symbol: 'HK$', name: 'Hong Kong Dollar', decimals: 2 },
  'NOK': { symbol: 'kr', name: 'Norwegian Krone', decimals: 2 },
  'TRY': { symbol: '₺', name: 'Turkish Lira', decimals: 2 },
  'RUB': { symbol: '₽', name: 'Russian Ruble', decimals: 2 },
  'INR': { symbol: '₹', name: 'Indian Rupee', decimals: 2 },
  'BRL': { symbol: 'R$', name: 'Brazilian Real', decimals: 2 },
  'ZAR': { symbol: 'R', name: 'South African Rand', decimals: 2 },
  'KRW': { symbol: '₩', name: 'South Korean Won', decimals: 0 },
  'PLN': { symbol: 'zł', name: 'Polish Zloty', decimals: 2 },
  'CZK': { symbol: 'Kč', name: 'Czech Koruna', decimals: 2 },
  'HUF': { symbol: 'Ft', name: 'Hungarian Forint', decimals: 0 },
  'DKK': { symbol: 'kr', name: 'Danish Krone', decimals: 2 },
  'ILS': { symbol: '₪', name: 'Israeli Shekel', decimals: 2 },
  'CLP': { symbol: '$', name: 'Chilean Peso', decimals: 0 },
  'PHP': { symbol: '₱', name: 'Philippine Peso', decimals: 2 },
  'AED': { symbol: 'د.إ', name: 'UAE Dirham', decimals: 2 },
  'COP': { symbol: '$', name: 'Colombian Peso', decimals: 2 },
  'SAR': { symbol: '﷼', name: 'Saudi Riyal', decimals: 2 },
  'MYR': { symbol: 'RM', name: 'Malaysian Ringgit', decimals: 2 },
  'RON': { symbol: 'lei', name: 'Romanian Leu', decimals: 2 },
  'BGN': { symbol: 'лв', name: 'Bulgarian Lev', decimals: 2 },
  'HRK': { symbol: 'kn', name: 'Croatian Kuna', decimals: 2 },
  'ISK': { symbol: 'kr', name: 'Icelandic Krona', decimals: 0 },
  'THB': { symbol: '฿', name: 'Thai Baht', decimals: 2 },
  'IDR': { symbol: 'Rp', name: 'Indonesian Rupiah', decimals: 0 },
  'VND': { symbol: '₫', name: 'Vietnamese Dong', decimals: 0 },
  'TWD': { symbol: 'NT$', name: 'Taiwan Dollar', decimals: 2 },
  'UAH': { symbol: '₴', name: 'Ukrainian Hryvnia', decimals: 2 },
  'QAR': { symbol: '﷼', name: 'Qatari Riyal', decimals: 2 },
  'KWD': { symbol: 'د.ك', name: 'Kuwaiti Dinar', decimals: 3 },
  'BHD': { symbol: '.د.ب', name: 'Bahraini Dinar', decimals: 3 },
  'OMR': { symbol: '﷼', name: 'Omani Rial', decimals: 3 },
  'JOD': { symbol: 'د.ا', name: 'Jordanian Dinar', decimals: 3 },
  'LBP': { symbol: '£', name: 'Lebanese Pound', decimals: 2 },
  'EGP': { symbol: '£', name: 'Egyptian Pound', decimals: 2 },
  'MAD': { symbol: 'د.م.', name: 'Moroccan Dirham', decimals: 2 },
  'TND': { symbol: 'د.ت', name: 'Tunisian Dinar', decimals: 3 },
  'DZD': { symbol: 'د.ج', name: 'Algerian Dinar', decimals: 2 },
  'LYD': { symbol: 'ل.د', name: 'Libyan Dinar', decimals: 3 },
  'NGN': { symbol: '₦', name: 'Nigerian Naira', decimals: 2 },
  'GHS': { symbol: '₵', name: 'Ghanaian Cedi', decimals: 2 },
  'KES': { symbol: 'KSh', name: 'Kenyan Shilling', decimals: 2 },
  'UGX': { symbol: 'USh', name: 'Ugandan Shilling', decimals: 0 },
  'TZS': { symbol: 'TSh', name: 'Tanzanian Shilling', decimals: 2 },
  'ETB': { symbol: 'Br', name: 'Ethiopian Birr', decimals: 2 },
} as const;

// Default and primary currencies
const DEFAULT_CURRENCY = 'USDT';
const PRIMARY_CURRENCIES = ['USDT', 'BTC', 'ETH', 'USD', 'EUR', 'GBP'] as const;

/**
 * Get currency symbol (synchronous version using local data)
 */
export function getCurrencySymbol(currency: string | null | undefined): string {
  if (!currency || typeof currency !== 'string') {
    return CRYPTO_CURRENCIES[DEFAULT_CURRENCY].symbol;
  }

  const upperCurrency = currency.toUpperCase();

  // Check crypto currencies first
  if (upperCurrency in CRYPTO_CURRENCIES) {
    return CRYPTO_CURRENCIES[upperCurrency as keyof typeof CRYPTO_CURRENCIES].symbol;
  }

  // Check fiat currencies
  if (upperCurrency in FIAT_CURRENCIES) {
    return FIAT_CURRENCIES[upperCurrency as keyof typeof FIAT_CURRENCIES].symbol;
  }

  // Return the currency code as fallback
  return upperCurrency;
}

/**
 * Get currency symbol with external library fallback (async version)
 */
export async function getCurrencySymbolAsync(currency: string | null | undefined): Promise<string> {
  if (!currency || typeof currency !== 'string') {
    return CRYPTO_CURRENCIES[DEFAULT_CURRENCY].symbol;
  }

  const upperCurrency = currency.toUpperCase();

  // Check crypto currencies first
  if (upperCurrency in CRYPTO_CURRENCIES) {
    return CRYPTO_CURRENCIES[upperCurrency as keyof typeof CRYPTO_CURRENCIES].symbol;
  }

  // Check fiat currencies
  if (upperCurrency in FIAT_CURRENCIES) {
    return FIAT_CURRENCIES[upperCurrency as keyof typeof FIAT_CURRENCIES].symbol;
  }

  // Try external library for additional fiat currencies
  try {
    const getSymbol = await loadCurrencySymbolMap();
    const externalSymbol = getSymbol?.(upperCurrency);
    if (externalSymbol) {
      return externalSymbol;
    }
  } catch (error) {
  }

  // Return the currency code as fallback
  return upperCurrency;
}

/**
 * Get currency information including symbol, name, and decimal places
 */
export async function getCurrencyInfo(currency: string | null | undefined): Promise<{
  code: string;
  symbol: string;
  name: string;
  decimals: number;
  type: 'fiat' | 'crypto' | 'unknown';
}> {
  if (!currency || typeof currency !== 'string') {
    const defaultInfo = CRYPTO_CURRENCIES[DEFAULT_CURRENCY];
    return {
      code: DEFAULT_CURRENCY,
      symbol: defaultInfo.symbol,
      name: defaultInfo.name,
      decimals: defaultInfo.decimals,
      type: 'crypto'
    };
  }

  const upperCurrency = currency.toUpperCase();

  // Check crypto currencies
  if (upperCurrency in CRYPTO_CURRENCIES) {
    const info = CRYPTO_CURRENCIES[upperCurrency as keyof typeof CRYPTO_CURRENCIES];
    return {
      code: upperCurrency,
      symbol: info.symbol,
      name: info.name,
      decimals: info.decimals,
      type: 'crypto'
    };
  }

  // Check fiat currencies
  if (upperCurrency in FIAT_CURRENCIES) {
    const info = FIAT_CURRENCIES[upperCurrency as keyof typeof FIAT_CURRENCIES];
    return {
      code: upperCurrency,
      symbol: info.symbol,
      name: info.name,
      decimals: info.decimals,
      type: 'fiat'
    };
  }

  // Try external library
  try {
    const getSymbol = await loadCurrencySymbolMap();
    const currencyCodes = await loadCurrencyCodes();
    
    const externalSymbol = getSymbol?.(upperCurrency);
    const externalInfo = (currencyCodes as { code?: (code: string) => { currency?: string } })?.code?.(upperCurrency);
    
    if (externalSymbol || externalInfo) {
      return {
        code: upperCurrency,
        symbol: externalSymbol || upperCurrency,
        name: externalInfo?.currency || upperCurrency,
        decimals: 2, // Default for most fiat currencies
        type: 'fiat'
      };
    }
  } catch (error) {
  }

  // Fallback for unknown currencies
  return {
    code: upperCurrency,
    symbol: upperCurrency,
    name: upperCurrency,
    decimals: 2,
    type: 'unknown'
  };
}

/**
 * Format a currency value for display (synchronous version)
 */
export function formatCurrencyValue(
  value: number | null | undefined, 
  currency: string | null | undefined
): string {
  // Handle null/undefined values
  if (value == null || currency == null || isNaN(Number(value))) {
    return '--';
  }

  const numValue = Number(value);
  const upperCurrency = currency.toUpperCase();
  
  // Get currency info from local data first
  let currencyInfo = null;
  if (upperCurrency in CRYPTO_CURRENCIES) {
    const info = CRYPTO_CURRENCIES[upperCurrency as keyof typeof CRYPTO_CURRENCIES];
    currencyInfo = { ...info, code: upperCurrency, type: 'crypto' as const };
  } else if (upperCurrency in FIAT_CURRENCIES) {
    const info = FIAT_CURRENCIES[upperCurrency as keyof typeof FIAT_CURRENCIES];
    currencyInfo = { ...info, code: upperCurrency, type: 'fiat' as const };
  } else {
    // Fallback for unknown currencies
    currencyInfo = {
      code: upperCurrency,
      symbol: upperCurrency,
      name: upperCurrency,
      decimals: 2,
      type: 'unknown' as const
    };
  }
  
  // Format the number with appropriate decimal places
  const formatted = numValue.toLocaleString(undefined, { 
    minimumFractionDigits: currencyInfo.decimals, 
    maximumFractionDigits: currencyInfo.decimals 
  });

  // For major fiat currencies and stablecoins, put symbol before the value
  if ((currencyInfo.type === 'fiat' && ['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(currencyInfo.code)) ||
      ['USDT', 'USDC', 'DAI', 'BUSD'].includes(currencyInfo.code)) {
    return `${currencyInfo.symbol}${formatted}`;
  }
  
  // For crypto currencies, put symbol after with space
  if (currencyInfo.type === 'crypto') {
    return `${formatted} ${currencyInfo.symbol}`;
  }
  
  // Default format: symbol prefix
  return `${currencyInfo.symbol}${formatted}`;
}

/**
 * Enhanced currency value formatting with extensive currency support (async version)
 */
export async function formatCurrencyValueAsync(
  value: number | null | undefined, 
  currency: string | null | undefined
): Promise<string> {
  // Handle null/undefined values
  if (value == null || currency == null || isNaN(Number(value))) {
    return '--';
  }

  const numValue = Number(value);
  const currencyInfo = await getCurrencyInfo(currency);
  
  // Format the number with appropriate decimal places
  const formatted = numValue.toLocaleString(undefined, { 
    minimumFractionDigits: currencyInfo.decimals, 
    maximumFractionDigits: currencyInfo.decimals 
  });

  // For major fiat currencies and stablecoins, put symbol before the value
  if ((currencyInfo.type === 'fiat' && ['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(currencyInfo.code)) ||
      ['USDT', 'USDC', 'DAI', 'BUSD'].includes(currencyInfo.code)) {
    return `${currencyInfo.symbol}${formatted}`;
  }
  
  // For crypto currencies, put symbol after with space
  if (currencyInfo.type === 'crypto') {
    return `${formatted} ${currencyInfo.symbol}`;
  }
  
  // Default format: symbol prefix
  return `${currencyInfo.symbol}${formatted}`;
}

/**
 * Extract the quote currency from a trading symbol (enhanced)
 */
export function getQuoteCurrencyFromSymbol(symbol: string | null): string {
  if (!symbol || typeof symbol !== 'string') {
    return DEFAULT_CURRENCY;
  }

  const upperSymbol = symbol.toUpperCase();
  
  // Enhanced pattern matching for various exchange formats
  const patterns = [
    // Standard patterns (BTCUSDT, ETHBTC)
    /([A-Z]{3,10})(USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
    // Binance style with numbers (BTC1000)
    /([A-Z]{3,10})(\d+)(USDT|USDC|USD)$/,
    // Hyphen or underscore separated (BTC-USD, BTC_USDT)
    /([A-Z]{3,10})[-_](USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
    // Slash separated (BTC/USD)
    /([A-Z]{3,10})\/(USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
    // Dot separated (BTC.USD)
    /([A-Z]{3,10})\.(USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
  ];

  for (const pattern of patterns) {
    const match = upperSymbol.match(pattern);
    if (match) {
      return match[match.length - 1]; // Return the last captured group (quote currency)
    }
  }

  // If no pattern matches, check if the symbol itself is a known currency
  if (upperSymbol in CRYPTO_CURRENCIES || upperSymbol in FIAT_CURRENCIES) {
    return upperSymbol;
  }

  return DEFAULT_CURRENCY;
}

/**
 * Extract the base currency from a trading symbol
 */
export function getBaseCurrencyFromSymbol(symbol: string | null): string {
  if (!symbol || typeof symbol !== 'string') {
    return 'BTC'; // Default fallback
  }

  const upperSymbol = symbol.toUpperCase();
  
  // Enhanced pattern matching for various exchange formats
  const patterns = [
    // Standard patterns (BTCUSDT, ETHBTC)
    /([A-Z]{3,10})(USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
    // Binance style with numbers (BTC1000)
    /([A-Z]{3,10})(\d+)(USDT|USDC|USD)$/,
    // Hyphen or underscore separated (BTC-USD, BTC_USDT)
    /([A-Z]{3,10})[-_](USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
    // Slash separated (BTC/USD)
    /([A-Z]{3,10})\/(USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
    // Dot separated (BTC.USD)
    /([A-Z]{3,10})\.(USDT|USDC|BUSD|DAI|USD|EUR|GBP|BTC|ETH)$/,
  ];

  for (const pattern of patterns) {
    const match = upperSymbol.match(pattern);
    if (match) {
      return match[1]; // Return the first captured group (base currency)
    }
  }

  // If no pattern matches, return the symbol itself if it's a known currency
  if (upperSymbol in CRYPTO_CURRENCIES || upperSymbol in FIAT_CURRENCIES) {
    return upperSymbol;
  }

  return 'BTC'; // Default fallback
}

/**
 * Get list of all supported currencies
 */
export function getSupportedCurrencies(): Array<{
  code: string;
  symbol: string;
  name: string;
  type: 'fiat' | 'crypto';
}> {
  const result = [];
  
  // Add crypto currencies
  for (const [code, info] of Object.entries(CRYPTO_CURRENCIES)) {
    result.push({
      code,
      symbol: info.symbol,
      name: info.name,
      type: 'crypto' as const
    });
  }
  
  // Add fiat currencies
  for (const [code, info] of Object.entries(FIAT_CURRENCIES)) {
    result.push({
      code,
      symbol: info.symbol,
      name: info.name,
      type: 'fiat' as const
    });
  }
  
  return result.sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Check if a currency is supported
 */
export function isSupportedCurrency(currency: string | null | undefined): boolean {
  if (!currency || typeof currency !== 'string') {
    return false;
  }
  
  const upperCurrency = currency.toUpperCase();
  return upperCurrency in CRYPTO_CURRENCIES || upperCurrency in FIAT_CURRENCIES;
}

/**
 * Check if a currency is supported (alias for backward compatibility)
 */
export function isValidCurrency(currency: string | null): boolean {
  return isSupportedCurrency(currency);
}

/**
 * Get the decimal places for a currency
 */
export function getDecimalPlaces(currency: string): number {
  if (!currency || typeof currency !== 'string') {
    return 2;
  }
  
  const upperCurrency = currency.toUpperCase();
  
  if (upperCurrency in CRYPTO_CURRENCIES) {
    return CRYPTO_CURRENCIES[upperCurrency as keyof typeof CRYPTO_CURRENCIES].decimals;
  }
  
  if (upperCurrency in FIAT_CURRENCIES) {
    return FIAT_CURRENCIES[upperCurrency as keyof typeof FIAT_CURRENCIES].decimals;
  }
  
  return 2; // Default for unknown currencies
}

/**
 * Get primary supported currencies for UI dropdowns
 */
export function getPrimaryCurrencies(): string[] {
  return [...PRIMARY_CURRENCIES];
}

// Re-export constants for backward compatibility
export const PORTFOLIO_CURRENCIES = {
  SUPPORTED: getPrimaryCurrencies(),
  DEFAULT: DEFAULT_CURRENCY,
  FIAT: Object.keys(FIAT_CURRENCIES),
  CRYPTO: Object.keys(CRYPTO_CURRENCIES),
  SYMBOLS: {
    ...Object.fromEntries(Object.entries(FIAT_CURRENCIES).map(([k, v]) => [k, v.symbol])),
    ...Object.fromEntries(Object.entries(CRYPTO_CURRENCIES).map(([k, v]) => [k, v.symbol]))
  }
};

// Icon path mapping for supported currencies
const CURRENCY_ICON_MAP: { [key: string]: string } = {
  // Fiat currencies with available icons
  'USD': '/node_modules/cryptocurrency-icons/svg/color/usd.svg',
  'EUR': '/node_modules/cryptocurrency-icons/svg/color/eur.svg',
  'GBP': '/node_modules/cryptocurrency-icons/svg/color/gbp.svg',
  'JPY': '/node_modules/cryptocurrency-icons/svg/color/jpy.svg',
  'CNY': '/node_modules/cryptocurrency-icons/svg/color/cny.svg',
  'RUB': '/node_modules/cryptocurrency-icons/svg/color/rub.svg',
  
  // Major cryptocurrencies
  'BTC': '/node_modules/cryptocurrency-icons/svg/color/btc.svg',
  'ETH': '/node_modules/cryptocurrency-icons/svg/color/eth.svg',
  'LTC': '/node_modules/cryptocurrency-icons/svg/color/ltc.svg',
  'XRP': '/node_modules/cryptocurrency-icons/svg/color/xrp.svg',
  'ADA': '/node_modules/cryptocurrency-icons/svg/color/ada.svg',
  'DOT': '/node_modules/cryptocurrency-icons/svg/color/dot.svg',
  'SOL': '/node_modules/cryptocurrency-icons/svg/color/sol.svg',
  'MATIC': '/node_modules/cryptocurrency-icons/svg/color/matic.svg',
  'AVAX': '/node_modules/cryptocurrency-icons/svg/color/avax.svg',
  'ATOM': '/node_modules/cryptocurrency-icons/svg/color/atom.svg',
  'LINK': '/node_modules/cryptocurrency-icons/svg/color/link.svg',
  'UNI': '/node_modules/cryptocurrency-icons/svg/color/uni.svg',
  'AAVE': '/node_modules/cryptocurrency-icons/svg/color/aave.svg',
  'COMP': '/node_modules/cryptocurrency-icons/svg/color/comp.svg',
  'MKR': '/node_modules/cryptocurrency-icons/svg/color/mkr.svg',
  'YFI': '/node_modules/cryptocurrency-icons/svg/color/yfi.svg',
  'SUSHI': '/node_modules/cryptocurrency-icons/svg/color/sushi.svg',
  'CRV': '/node_modules/cryptocurrency-icons/svg/color/crv.svg',
  'BAL': '/node_modules/cryptocurrency-icons/svg/color/bal.svg',
  
  // Stablecoins
  'USDT': '/node_modules/cryptocurrency-icons/svg/color/usdt.svg',
  'USDC': '/node_modules/cryptocurrency-icons/svg/color/usdc.svg',
  'TUSD': '/node_modules/cryptocurrency-icons/svg/color/tusd.svg',
  'GUSD': '/node_modules/cryptocurrency-icons/svg/color/gusd.svg',
  'DAI': '/node_modules/cryptocurrency-icons/svg/color/dai.svg',
  'PAX': '/node_modules/cryptocurrency-icons/svg/color/pax.svg',
  
  // Exchange tokens
  'BNB': '/node_modules/cryptocurrency-icons/svg/color/bnb.svg',
  'OKB': '/node_modules/cryptocurrency-icons/svg/color/ok.svg',
  'KCS': '/node_modules/cryptocurrency-icons/svg/color/kcs.svg',
  'HT': '/node_modules/cryptocurrency-icons/svg/color/ht.svg',
  'GT': '/node_modules/cryptocurrency-icons/svg/color/generic.svg', // Fallback for GT
  
  // Layer 1 & Alt coins
  'EOS': '/node_modules/cryptocurrency-icons/svg/color/eos.svg',
  'TRX': '/node_modules/cryptocurrency-icons/svg/color/trx.svg',
  'XLM': '/node_modules/cryptocurrency-icons/svg/color/xlm.svg',
  'VET': '/node_modules/cryptocurrency-icons/svg/color/vet.svg',
  'THETA': '/node_modules/cryptocurrency-icons/svg/color/theta.svg',
  'FIL': '/node_modules/cryptocurrency-icons/svg/color/fil.svg',
  'XTZ': '/node_modules/cryptocurrency-icons/svg/color/xtz.svg',
  'NEO': '/node_modules/cryptocurrency-icons/svg/color/neo.svg',
  'WAVES': '/node_modules/cryptocurrency-icons/svg/color/waves.svg',
  'ZEC': '/node_modules/cryptocurrency-icons/svg/color/zec.svg',
  'DASH': '/node_modules/cryptocurrency-icons/svg/color/dash.svg',
  'XMR': '/node_modules/cryptocurrency-icons/svg/color/xmr.svg',
  'DCR': '/node_modules/cryptocurrency-icons/svg/color/dcr.svg',
  'ZIL': '/node_modules/cryptocurrency-icons/svg/color/zil.svg',
  'ONT': '/node_modules/cryptocurrency-icons/svg/color/ont.svg',
  'QTUM': '/node_modules/cryptocurrency-icons/svg/color/qtum.svg',
  'LSK': '/node_modules/cryptocurrency-icons/svg/color/lsk.svg',
  'NANO': '/node_modules/cryptocurrency-icons/svg/color/nano.svg',
  'DOGE': '/node_modules/cryptocurrency-icons/svg/color/doge.svg',
  'BCH': '/node_modules/cryptocurrency-icons/svg/color/bch.svg',
  'BSV': '/node_modules/cryptocurrency-icons/svg/color/bsv.svg',
  'ETC': '/node_modules/cryptocurrency-icons/svg/color/etc.svg',
};

/**
 * Get the icon path for a currency
 * @param currency - The currency code
 * @returns The path to the currency icon SVG, or null if no icon is available
 */
export function getCurrencyIcon(currency: string | null | undefined): string | null {
  if (!currency || typeof currency !== 'string') {
    return CURRENCY_ICON_MAP[DEFAULT_CURRENCY] || null;
  }

  const upperCurrency = currency.toUpperCase();
  return CURRENCY_ICON_MAP[upperCurrency] || null;
}

/**
 * Check if a currency has an available icon
 * @param currency - The currency code
 * @returns True if an icon is available for the currency
 */
export function hasCurrencyIcon(currency: string | null | undefined): boolean {
  if (!currency || typeof currency !== 'string') {
    return !!CURRENCY_ICON_MAP[DEFAULT_CURRENCY];
  }

  const upperCurrency = currency.toUpperCase();
  return !!CURRENCY_ICON_MAP[upperCurrency];
}

/**
 * Get all currencies that have available icons
 * @returns Array of currency codes that have icons
 */
export function getCurrenciesWithIcons(): string[] {
  return Object.keys(CURRENCY_ICON_MAP);
}
